import React from 'react';

import { Flex, Label, LssProp, Text } from '@lucidhq/lucidium';
import { Field } from '@lucidhq/lucidium/components/FormV2';

import { InfoTooltip } from '~/impact-measurement/components';
import { Channel } from '~/impact-measurement/pages/types';
import { t } from '~/utils/i18n';

import { CHANNELS } from './helpers';

export enum IMPRESSIONS_FIELD_NAME {
  'digital' = 'expected_impressions_count.digital',
  'linear_tv' = 'expected_impressions_count.linear_tv',
  'social' = 'expected_impressions_count.social'
}

export enum IMPRESSIONS_FIELD_LABEL {
  'digital' = 'Number of Impressions (Digital)',
  'linear_tv' = 'Number of Impressions (Linear TV)',
  'social' = 'Number of Impressions (Social)'
}

const fieldSteps: Record<CHANNELS, number> = {
  digital: 500_000,
  linear_tv: 10_000_000,
  social: 500_000
};

const impressionsContainerLss: LssProp = {
  flexDirection: 'row',
  gap: '4px',
  alignItems: 'center',
  justifyContent: 'center'
};

type ImpressionsFieldProps = {
  isChecked: boolean;
  type: Channel;
  impressionsValue?: number;
  isDisabled?: boolean;
};

export const ImpressionsField: React.FC<ImpressionsFieldProps> = ({
  isChecked,
  type,
  impressionsValue,
  isDisabled
}) => {
  const impressionsLocale = impressionsValue?.toLocaleString('en-US');
  const hasImpressions = !!impressionsValue && impressionsLocale !== '0';

  return (
    <>
      <Flex lss={impressionsContainerLss}>
        <Label
          lss={{ my: 'sm', mr: 'xxs' }}
          required={isChecked}
          htmlFor={IMPRESSIONS_FIELD_NAME[type as CHANNELS]}
        >
          {t(`forms.labels.impressions.${type}`)}
        </Label>
        <InfoTooltip
          title={t('campaignManager.campaignDetails.tooltips.impressions')}
          placement="top-start"
          iconLss={{ height: '1.125rem', width: '1.125rem' }}
        />
        {hasImpressions && (
          <Text lss={{ pl: 'xs', pb: 'xxs' }}>
            {t('labels.entered')}
            <Text
              as="span"
              lss={{
                textScale: 'heading6',
                color: 'secondary500',
                pl: 'xs',
                fontWeight: 'medium'
              }}
            >
              {impressionsLocale}
            </Text>
          </Text>
        )}
      </Flex>
      <Field
        name={IMPRESSIONS_FIELD_NAME[type as CHANNELS]}
        type="number"
        step={fieldSteps[type as CHANNELS]}
        disabled={!isChecked || isDisabled}
        placeholder={t(`campaignManager.campaignDetails.placeholders.${type}`)}
        value={impressionsValue || ''}
        groupLss={{ pb: 'none', width: '100%' }}
      />
    </>
  );
};
